import { NewUserTemporaryAccess } from '@order-management/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Section } from 'react-bulma-components';
import { FaCheck } from 'react-icons/fa6';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { environment } from '../../../environments/environment';
import CancelAlertModal from '../../shared/cancel-alert-modal/cancel-alert-modal';
import useHttpRequest from '../../shared/hooks/http-request';
import useUser from '../../shared/hooks/user';
import { extractEmailDomain, isEmail } from '../../shared/utils';
import Button from '../../ui/button/button';
import { grantTemporaryAccess } from '../admin-service';
import {
  TemporaryAccessSetupPageContext,
  TemporaryAccessSetupSteps,
} from '../admin-temporary-access-setup-page/types';
import { SalesOrdersAccessStorageService } from '../sales-orders-access-storage-service';
import TemporaryAccessSetupCompletedConfirmationModal from '../temporary-access-setup-completed-confirmation-modal/temporary-access-setup-completed-confirmation-modal';
import InviteTemporaryAccessUserForm, {
  InviteTemporaryAccessUserDetailsFormData,
  InviteTemporaryAccessUserDetailsFormValidation,
} from './invite-temporary-access-user-details-form/invite-temporary-access-user-details-form';

const MAX_PERMISSION_DAYS_DURATION = Number(
  environment.temporaryUserPermissionMaxDaysDuration
);

const temporaryAccessSalesOrdersSessionStorageService =
  new SalesOrdersAccessStorageService();

const INVALID_EMAIL_ERROR = 'Invalid format';
const EMAIL_MISMATCH_ERROR = 'Emails don’t match';
const INVALID_EMAIL_DOMAIN_ERROR =
  'The email you provided does not have a valid domain';
const CANCEL_BUTTON_TEXT = 'Cancel';
const SUBMIT_BUTTON_TEXT = 'Submit';
const CANCEL_ALERT_MODAL_TITLE = 'Cancel grant temporary support access';
const CANCEL_ALERT_MODAL_SUBTITLE =
  'Are you sure you want to cancel the temporary support access granting process?';
const GRANT_TEMPORARY_PERMISSIONS_ERROR =
  'An error occurred while granting the selected permissions to the user';

export function AdminTemporaryAccessUserDetailsPage() {
  const navigate = useNavigate();
  const { setStep, setHeaderButton } =
    useOutletContext<TemporaryAccessSetupPageContext>();
  const { executeHttpRequest, isLoading } = useHttpRequest();
  const { user, refetchUser, isRefetching } = useUser();
  const [submitButtonPressed, setInviteUserButtonPressed] =
    useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [showCancelAlertModal, setShowCancelAlertModal] =
    useState<boolean>(false);

  const allowedInternalEmailDomains = useMemo(
    () => environment.internalUserEmailDomains.split(','),
    []
  );

  const invalidDaysDurationError = useMemo(() => {
    if (MAX_PERMISSION_DAYS_DURATION === 1) {
      return 'Temporary access has to be granted for 1 day';
    }
    return `Temporary access has to be granted between 1 and ${MAX_PERMISSION_DAYS_DURATION} days`;
  }, []);

  const initialFormData: InviteTemporaryAccessUserDetailsFormData =
    useMemo(() => {
      return {
        email: { value: '', touched: false, error: null },
        emailConfirmation: { value: '', touched: false, error: null },
        daysDuration: { value: '10', touched: false, error: null },
      };
    }, []);

  const [formData, setFormData] =
    useState<InviteTemporaryAccessUserDetailsFormData>(initialFormData);

  const validateForm: InviteTemporaryAccessUserDetailsFormValidation = useMemo(
    () => ({
      email: (value: string) => {
        if (!value || !isEmail(value)) {
          return INVALID_EMAIL_ERROR;
        }

        const emailDomain = extractEmailDomain(value) as string;
        if (!allowedInternalEmailDomains.includes(emailDomain)) {
          return INVALID_EMAIL_DOMAIN_ERROR;
        }

        return null;
      },
      emailConfirmation: (value: string) => {
        if (!value || !isEmail(value)) {
          return INVALID_EMAIL_ERROR;
        }
        return null;
      },
      daysDuration: (value: string) => {
        if (
          !value ||
          Number(value) < 1 ||
          Number(value) > MAX_PERMISSION_DAYS_DURATION
        ) {
          return invalidDaysDurationError;
        }
        return null;
      },
    }),
    [allowedInternalEmailDomains, invalidDaysDurationError]
  );

  const showEmailMismatchError = useMemo(() => {
    const hasInputValues =
      formData['email'].touched && formData['emailConfirmation'].touched;
    const hasValidValues =
      !formData['email'].error && !formData['emailConfirmation'].error;
    if (hasInputValues && hasValidValues) {
      return formData['email'].value !== formData['emailConfirmation'].value;
    }
    return false;
  }, [formData]);

  const formError = useMemo(() => {
    if (showEmailMismatchError) {
      return EMAIL_MISMATCH_ERROR;
    }
    return null;
  }, [showEmailMismatchError]);

  const validateData = useCallback(() => {
    return Object.keys(formData).reduce(
      (acc, key) => {
        const formDataKey =
          key as keyof InviteTemporaryAccessUserDetailsFormData;
        const error = validateForm[formDataKey]?.(formData[formDataKey].value);
        setFormData((prev) => ({
          ...prev,
          [formDataKey]: { ...prev[formDataKey], error, touched: true },
        }));
        return {
          values: { ...acc.values, [key]: formData[formDataKey].value },
          errors: { ...acc.errors, [key]: error },
        };
      },
      { values: {}, errors: {} }
    );
  }, [formData, validateForm]);

  const handleGrantTemporaryAccess = useCallback(
    async () =>
      executeHttpRequest({
        asyncFunction: async () => {
          setInviteUserButtonPressed(true);
          const formValidation = validateData();
          const isValidForm =
            Object.values(formValidation.errors).every(
              (fieldError) => !fieldError
            ) && !formError;
          if (isValidForm) {
            const newUserTemporaryAccess: NewUserTemporaryAccess = {
              email: formData.email.value,
              daysDuration: Number(formData.daysDuration.value),
              salesOrders: temporaryAccessSalesOrdersSessionStorageService
                .get()
                .map((so) => so.id),
            };
            await grantTemporaryAccess(newUserTemporaryAccess);

            setShowConfirmationModal(true);
          }
        },
        customErrorMessage: GRANT_TEMPORARY_PERMISSIONS_ERROR,
      }),
    [
      executeHttpRequest,
      formData.daysDuration.value,
      formData.email.value,
      formError,
      validateData,
    ]
  );

  const refreshUserPermissions = useCallback(async () => {
    // If the current user has self assigned new permissions, their data should be refetched
    const formEmail = formData.email.value.trim().toLowerCase();
    if (formEmail === user.email) {
      await refetchUser();
    }
  }, [formData.email.value, refetchUser, user.email]);

  const navigateToTemporaryAccessManagementPage = useCallback(async () => {
    await refreshUserPermissions();
    navigate('/admin/temporary-access');
  }, [navigate, refreshUserPermissions]);

  useEffect(() => {
    setStep(TemporaryAccessSetupSteps.USER);
    setHeaderButton(undefined);
  }, [setHeaderButton, setStep]);

  return (
    <>
      <Section paddingless>
        <InviteTemporaryAccessUserForm
          data={formData}
          onChangeData={setFormData}
          validate={validateForm}
          formError={formError}
          submitButtonPressed={submitButtonPressed}
        />
      </Section>
      <Section
        paddingless
        display="flex"
        justifyContent={'flex-end'}
        alignItems={'flex-end'}
        className="gap-6 is-flex-grow-1 pt-0 pl-4"
      >
        <Button
          color="primary-dark"
          outlined
          onClick={() => setShowCancelAlertModal(true)}
        >
          {CANCEL_BUTTON_TEXT}
        </Button>
        <Button
          color="primary-dark"
          onClick={handleGrantTemporaryAccess}
          isExecutingAction={isLoading}
        >
          <Button.LoadingIcon icon={FaCheck} />
          <span>{SUBMIT_BUTTON_TEXT}</span>
        </Button>
      </Section>

      <TemporaryAccessSetupCompletedConfirmationModal
        show={showConfirmationModal}
        email={formData.email.value}
        navigateToTemporaryAccessDashboardPage={
          navigateToTemporaryAccessManagementPage
        }
        loading={isRefetching}
      />
      <CancelAlertModal
        show={showCancelAlertModal}
        title={CANCEL_ALERT_MODAL_TITLE}
        subtitle={CANCEL_ALERT_MODAL_SUBTITLE}
        onCancel={navigateToTemporaryAccessManagementPage}
        onClose={() => setShowCancelAlertModal(false)}
      />
    </>
  );
}

export default AdminTemporaryAccessUserDetailsPage;
